import { useMemo, useState } from 'react';
import {
  RiAddLine,
  RiDeleteBin6Line,
  RiNodeTree,
  RiSearchLine,
  RiShareForwardLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useStore } from '../../state/store';
import { useCanvasStore } from '../../canvas/canvasStore';
import { DRAG_MIME, type DragPayload } from '../../canvas/drag';
import { entityIcon, labelForType } from '../icons';
import { ENTITY_STATUS_LABEL, type EntityStatus } from '../../state/domain';
import { cn } from '@/lib/utils';

/** 生成拖拽载荷（侧栏 → 关联墙）。 */
export function dragProps(payload: DragPayload): {
  draggable: true;
  onDragStart: (event: React.DragEvent) => void;
} {
  return {
    draggable: true,
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
      event.dataTransfer.effectAllowed = 'copy';
    },
  };
}

export function EntitiesPanel() {
  const entities = useStore(state => state.entities);
  const relations = useStore(state => state.relations);
  const activeTargetId = useStore(state => state.activeTargetId);
  const addEntity = useStore(state => state.addEntity);
  const deleteEntity = useStore(state => state.deleteEntity);
  const addRelation = useStore(state => state.addRelation);
  const deleteRelation = useStore(state => state.deleteRelation);
  const controller = useCanvasStore(state => state.controller);

  const [filter, setFilter] = useState('');
  const [newEntity, setNewEntity] = useState({
    type: 'domain',
    value: '',
    label: '',
    aliases: '',
    tags: '',
    status: 'unverified' as EntityStatus,
  });
  const [relationForm, setRelationForm] = useState({ sourceId: '', targetId: '', type: 'link', label: '' });

  const grouped = useMemo(() => {
    const map = new Map<string, typeof entities>();
    for (const entity of entities) {
      const haystack = `${entity.type} ${entity.value} ${entity.label}`.toLowerCase();
      if (filter && !haystack.includes(filter.toLowerCase())) continue;
      const list = map.get(entity.type) ?? [];
      list.push(entity);
      map.set(entity.type, list);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [entities, filter]);

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">线索台账</span>
        <Badge variant="muted">
          {entities.length} 线索 / {relations.length} 关系
        </Badge>
      </div>

      <div className="relative">
        <RiSearchLine className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-7" placeholder="检索线索…" value={filter} onChange={e => setFilter(e.target.value)} />
      </div>

      <details className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <RiAddLine className="size-3.5" />
            手工登记线索
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>类型</Label>
              <Input value={newEntity.type} onChange={e => setNewEntity({ ...newEntity, type: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>标签（可选）</Label>
              <Input value={newEntity.label} onChange={e => setNewEntity({ ...newEntity, label: e.target.value })} />
            </div>
          </div>
          <Label>值</Label>
          <Input
            value={newEntity.value}
            onChange={e => setNewEntity({ ...newEntity, value: e.target.value })}
            placeholder="example.com / a@b.com / @handle"
          />
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>核实状态</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={newEntity.status}
                onChange={e => setNewEntity({ ...newEntity, status: e.target.value as EntityStatus })}
              >
                {(Object.keys(ENTITY_STATUS_LABEL) as EntityStatus[]).map(status => (
                  <option key={status} value={status}>
                    {ENTITY_STATUS_LABEL[status]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>别名（逗号分隔）</Label>
              <Input value={newEntity.aliases} onChange={e => setNewEntity({ ...newEntity, aliases: e.target.value })} />
            </div>
          </div>
          <Label>标签（逗号分隔）</Label>
          <Input value={newEntity.tags} onChange={e => setNewEntity({ ...newEntity, tags: e.target.value })} />
          <Button
            size="sm"
            onClick={async () => {
              if (!newEntity.value.trim()) return;
              const split = (v: string) =>
                v
                  .split(/[,，\s]+/)
                  .map(t => t.trim())
                  .filter(Boolean);
              await addEntity({
                targetId: activeTargetId,
                type: newEntity.type,
                value: newEntity.value.trim(),
                label: newEntity.label || newEntity.value.trim(),
                aliases: split(newEntity.aliases),
                tags: split(newEntity.tags),
                status: newEntity.status,
                confidence: 0.6,
                source: 'manual',
              });
              setNewEntity({ type: 'domain', value: '', label: '', aliases: '', tags: '', status: 'unverified' });
            }}
          >
            登记
          </Button>
        </div>
      </details>

      <details className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <RiShareForwardLine className="size-3.5" />
            登记关联关系
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>起点</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={relationForm.sourceId}
                onChange={e => setRelationForm({ ...relationForm, sourceId: e.target.value })}
              >
                <option value="">选择</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.value ?? e.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>终点</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={relationForm.targetId}
                onChange={e => setRelationForm({ ...relationForm, targetId: e.target.value })}
              >
                <option value="">选择</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.value ?? e.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Label>关系类型</Label>
          <Input
            value={relationForm.type}
            onChange={e => setRelationForm({ ...relationForm, type: e.target.value })}
            placeholder="works_at / owns / communicates_with"
          />
          <Button
            size="sm"
            onClick={async () => {
              if (!relationForm.sourceId || !relationForm.targetId) return;
              await addRelation({ ...relationForm, confidence: 0.6 });
              setRelationForm({ sourceId: '', targetId: '', type: 'link', label: '' });
            }}
          >
            登记关系
          </Button>
        </div>
      </details>

      {grouped.map(([type, list]) => {
        const Icon = entityIcon(type);
        return (
          <div key={type} className="overflow-hidden rounded-[var(--radius)] border border-border">
            <div className="flex items-center gap-1.5 border-b border-border bg-muted/50 px-2.5 py-1.5">
              <Icon className="size-3.5 text-muted-foreground" />
              <span className="text-[11px] font-medium">{labelForType(type)}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{list.length}</span>
            </div>
            <div className="divide-y divide-border">
              {list.map(entity => (
                <div
                  key={entity.id}
                  className="group cursor-grab px-2.5 py-1.5 active:cursor-grabbing"
                  {...dragProps({
                    kind: 'entity',
                    id: entity.id,
                    title: `${labelForType(entity.type)}｜${entity.value ?? entity.label ?? ''}`,
                    text: [
                      entity.aliases.length ? `别名：${entity.aliases.join('、')}` : '',
                      `置信度 ${(entity.confidence * 100).toFixed(0)}% · ${ENTITY_STATUS_LABEL[entity.status]}`,
                      entity.source ? `来源 ${entity.source}` : '',
                    ]
                      .filter(Boolean)
                      .join('\n'),
                    color: 'green',
                  })}
                  title="拖到关联墙上"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-[11px]">{entity.value ?? entity.label}</span>
                    <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        title="放置到关联图"
                        onClick={() => {
                          if (!controller) return;
                          controller.addEntityNode(controller.viewport.center, {
                            title: `${labelForType(entity.type)}｜${entity.value ?? entity.label ?? ''}`,
                            text: `置信度 ${(entity.confidence * 100).toFixed(0)}%${entity.source ? `\n来源 ${entity.source}` : ''}`,
                            color: 'green',
                            refKind: 'entity',
                            refId: entity.id,
                          });
                        }}
                      >
                        <RiNodeTree className="size-3.5" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => deleteEntity(entity.id)}
                      >
                        <RiDeleteBin6Line className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                    <span>置信度 {(entity.confidence * 100).toFixed(0)}%</span>
                    <span
                      className={cn(
                        entity.status === 'verified' && 'text-[var(--color-evidence)]',
                        entity.status === 'disputed' && 'text-[var(--color-cord)]'
                      )}
                    >
                      {ENTITY_STATUS_LABEL[entity.status]}
                    </span>
                    {entity.geo?.place && <span>{entity.geo.place}</span>}
                    {entity.source && <span className="truncate">{entity.source}</span>}
                  </div>
                  {(entity.aliases.length > 0 || entity.tags.length > 0) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {entity.aliases.map(alias => (
                        <Badge key={alias} variant="muted">
                          {alias}
                        </Badge>
                      ))}
                      {entity.tags.map(tag => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {relations.length > 0 && (
        <div className="overflow-hidden rounded-[var(--radius)] border border-border">
          <div className="border-b border-border bg-muted/50 px-2.5 py-1.5 text-[11px] font-medium">关系</div>
          <div className="divide-y divide-border">
            {relations.map(relation => {
              const source = entities.find(e => e.id === relation.sourceId);
              const target = entities.find(e => e.id === relation.targetId);
              return (
                <div key={relation.id} className="group flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px]">
                  <span className="truncate">{source?.value ?? relation.sourceId.slice(0, 6)}</span>
                  <span className="shrink-0 rounded bg-[var(--color-lead)]/10 px-1 font-mono text-[9.5px] text-[var(--color-lead)]">
                    {relation.label ?? relation.type}
                  </span>
                  <span className="truncate">{target?.value ?? relation.targetId.slice(0, 6)}</span>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    className={cn('ml-auto text-muted-foreground hover:text-destructive')}
                    onClick={() => deleteRelation(relation.id)}
                  >
                    <RiDeleteBin6Line className="size-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {entities.length === 0 && (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground">
          尚无线索。可在「检索」页采集，或手工登记。
        </div>
      )}
    </div>
  );
}